import React, { useState, useEffect } from 'react';
import {
  HardHat,
  Wrench,
  Zap,
  Cpu,
  PlayCircle,
  Users,
  UserCog,
  LifeBuoy,
  CheckCircle2,
  ChevronDown,
} from 'lucide-react';

const services = [
  {
    id: 'site-supervision',
    icon: HardHat,
    title: 'Site Supervision',
    description:
      "Experienced site supervisors and engineers supporting industrial installation and project execution on behalf of the customer.",
    bullets: [
      'Installation supervision',
      'Work progress monitoring',
      'Quality and specification checks',
      'Coordination with contractors and site teams',
      'Technical issue follow-up',
      'Progress reporting',
      'Safety and project requirement coordination',
    ],
  },
  {
    id: 'mechanical-installation-support',
    icon: Wrench,
    title: 'Mechanical Installation Support',
    description:
      'Mechanical engineers, supervisors and technical specialists supporting machinery and production equipment installation.',
    bullets: [
      'Machinery positioning and alignment',
      'Mechanical assembly supervision',
      'Equipment installation support',
      'Conveyor and production line installation',
      'Mechanical troubleshooting',
      'Installation inspection',
      'Preparation for commissioning',
    ],
  },
  {
    id: 'electrical-installation-support',
    icon: Zap,
    title: 'Electrical Installation Support',
    description:
      'Electrical engineers, supervisors and technicians for industrial electrical installation and project execution.',
    bullets: [
      'Electrical installation supervision',
      'Cable routing and cable installation coordination',
      'Control cabinet and field device installation',
      'Electrical connection checks',
      'I/O and signal verification',
      'Electrical testing support',
      'Troubleshooting during installation and commissioning',
    ],
  },
  {
    id: 'automation-plc-support',
    icon: Cpu,
    title: 'Automation & PLC Support',
    description:
      'Automation engineers and technical specialists supporting commissioning, troubleshooting and industrial control systems.',
    bullets: [
      'PLC commissioning support',
      'I/O testing',
      'Sensor and actuator verification',
      'Control system troubleshooting',
      'Parameter adjustment',
      'Production line start-up support',
      'Coordination with machinery manufacturers and customer automation teams',
    ],
  },
  {
    id: 'commissioning-startup',
    icon: PlayCircle,
    title: 'Commissioning & Start-up Support',
    description:
      'Engineering personnel supporting equipment commissioning, testing and production start-up.',
    bullets: [
      'Pre-commissioning checks',
      'Equipment testing',
      'Functional verification',
      'Start-up assistance',
      'Troubleshooting',
      'Punch-list follow-up',
      'Coordination between OEM, contractors and customer teams',
      'Production ramp-up support',
    ],
  },
  {
    id: 'project-coordination',
    icon: Users,
    title: 'Project Coordination',
    description:
      'Local engineering and technical coordination for industrial projects being executed in China.',
    bullets: [
      'Coordination with local contractors',
      'Communication with suppliers and site teams',
      'Technical follow-up',
      'Schedule and progress coordination',
      'Site issue management',
      'Project reporting',
      'Coordination between overseas customer teams and local project participants',
    ],
  },
  {
    id: 'technical-personnel',
    icon: UserCog,
    title: 'Technical Personnel',
    description:
      'Qualified technical personnel for short-term and long-term industrial project assignments.',
    listLabel: 'Personnel may include:',
    bullets: [
      'Mechanical engineers',
      'Electrical engineers',
      'Automation / PLC engineers',
      'Site supervisors',
      'Commissioning engineers',
      'Mechanical supervisors',
      'Electrical supervisors',
      'Industrial technicians',
      'Project coordinators',
    ],
    footnote:
      'Personnel can be provided individually or as part of a project team depending on the project requirements.',
  },
  {
    id: 'troubleshooting-support',
    icon: LifeBuoy,
    title: 'Troubleshooting & Technical Support',
    description:
      'On-site engineering support when technical problems arise during installation, commissioning or operation.',
    bullets: [
      'Mechanical troubleshooting',
      'Electrical troubleshooting',
      'Automation and control system troubleshooting',
      'Equipment fault investigation',
      'Technical coordination with OEMs',
      'Corrective action follow-up',
      'On-site technical assistance',
    ],
  },
];

const ServiceCard = ({ service, index }) => {
  const [expanded, setExpanded] = useState(false);
  const Icon = service.icon;
  const listLabel = service.listLabel || 'Typical Support:';

  useEffect(() => {
    const openIfMatching = () => {
      if (window.location.hash === `#${service.id}`) {
        setExpanded(true);
      }
    };
    openIfMatching();
    window.addEventListener('hashchange', openIfMatching);
    return () => window.removeEventListener('hashchange', openIfMatching);
  }, [service.id]);

  return (
    <div
      id={service.id}
      data-testid={`service-card-${service.id}`}
      className="group scroll-mt-28 bg-white rounded-2xl p-7 md:p-8 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-blue-200 flex flex-col"
      style={{ animationDelay: `${index * 60}ms` }}
    >
      {/* Icon */}
      <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-blue-600 to-blue-800 rounded-xl mb-5 shadow-md group-hover:scale-105 transition-transform">
        <Icon className="w-7 h-7 text-white" />
      </div>

      {/* Title */}
      <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3 leading-tight">
        {service.title}
      </h3>

      {/* Description */}
      <p className="text-gray-600 text-sm md:text-base mb-5 leading-relaxed">
        {service.description}
      </p>

      {/* Mobile toggle */}
      <button
        type="button"
        data-testid={`service-toggle-${service.id}`}
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className="md:hidden inline-flex items-center justify-between w-full text-sm font-semibold text-blue-700 hover:text-blue-800 border border-blue-100 bg-blue-50 hover:bg-blue-100 rounded-lg px-4 py-2.5 transition-colors"
      >
        <span>{expanded ? 'Hide Details' : 'View Details'}</span>
        <ChevronDown
          className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Bullet list */}
      <div
        className={`mt-5 space-y-2.5 ${expanded ? 'block' : 'hidden'} md:block`}
        data-testid={`service-details-${service.id}`}
      >
        <p className="text-xs font-bold tracking-wider text-blue-800 uppercase mb-3">
          {listLabel}
        </p>
        <ul className="space-y-2">
          {service.bullets.map((bullet, i) => (
            <li key={i} className="flex items-start">
              <CheckCircle2 className="w-4 h-4 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
              <span className="text-gray-700 text-sm leading-relaxed">
                {bullet}
              </span>
            </li>
          ))}
        </ul>
        {service.footnote && (
          <p className="text-sm text-gray-600 italic pt-3 mt-2 border-t border-gray-100 leading-relaxed">
            {service.footnote}
          </p>
        )}
      </div>
    </div>
  );
};

export const Services = () => {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-24 bg-gradient-to-br from-gray-50 via-blue-50 to-gray-50"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mx-auto">
          {/* Section Header */}
          <div className="mb-14 max-w-6xl">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Our Core Services
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-blue-800 mb-6"></div>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl leading-relaxed">
              Engineering and technical support for industrial projects across China.
            </p>
          </div>

          {/* Services Grid */}
          <div
            data-testid="services-grid"
            className="services-grid gap-6 md:gap-7"
          >
            {services.map((service, index) => (
              <ServiceCard key={service.id} service={service} index={index} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
